import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useSettings } from "../context/SettingsContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Save, Trash2, Plus, Settings as Cog, BookOpen, MessageSquare, Edit3, X, GripVertical, RefreshCw } from "lucide-react";

function TabButton({ active, onClick, icon: Icon, label, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition ${
        active ? "bg-amber-500/15 text-amber-300 border border-amber-400/40" : "text-slate-400 hover:text-white hover:bg-white/5 border border-transparent"
      }`}
    >
      <Icon size={16} /> {label}
    </button>
  );
}

function SettingsPanel() {
  const { settings, refresh } = useSettings();
  const [form, setForm] = useState(settings);
  const [saving, setSaving] = useState(false);
  useEffect(() => setForm(settings), [settings]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", form);
      await refresh();
      toast.success("Settings updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const fields = [
    ["windows_download_url", "Windows Download URL (.exe)"],
    ["android_download_url", "Android Download URL (.apk)"],
    ["stat_students", "Stat: Total Students"],
    ["stat_youtube_views", "Stat: YouTube Views"],
    ["stat_watch_time", "Stat: Watch Time"],
    ["telegram_username", "Telegram Username"],
    ["support_email", "Support Email"],
    ["youtube_url", "YouTube Channel URL"],
    ["instagram_url", "Instagram Profile URL"],
    ["whatsapp_number", "WhatsApp Number (with country code, e.g. 919999999999)"],
    ["whatsapp_message", "WhatsApp Pre-filled Message"],
    ["site_url", "Public Site URL (e.g. https://aducation.in) — used in sitemap.xml"],
    ["ga4_measurement_id", "Google Analytics 4 Measurement ID (e.g. G-XXXXXXXXXX)"],
  ];

  return (
    <div className="space-y-4">
      <h2 className="font-display font-bold text-xl">Global Settings</h2>

      <div>
        <label className="text-xs uppercase tracking-widest text-slate-400 font-semibold block mb-2">
          Floating Chat Button (show one on all pages)
        </label>
        <select
          data-testid="settings-primary_chat_channel"
          value={form.primary_chat_channel || "telegram"}
          onChange={(e) => set("primary_chat_channel", e.target.value)}
          className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-amber-400/50 text-sm"
        >
          <option value="telegram">Telegram (uses Telegram Username above)</option>
          <option value="whatsapp">WhatsApp (uses WhatsApp Number below)</option>
          <option value="none">None — hide floating button</option>
        </select>
        <p className="text-[11px] text-slate-500 mt-1.5">Only one floating button shows on every page. Empty username/number auto-hides the button.</p>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        {fields.map(([k, label]) => (
          <div key={k}>
            <label className="text-xs uppercase tracking-widest text-slate-400 font-semibold block mb-2">{label}</label>
            <input
              data-testid={`settings-${k}`}
              value={form[k] || ""}
              onChange={(e) => set(k, e.target.value)}
              className="w-full px-4 py-2.5 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-amber-400/50 text-sm"
            />
          </div>
        ))}
      </div>
      <button
        data-testid="settings-save-btn"
        onClick={save}
        disabled={saving}
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gradient-to-r from-[#E50914] to-[#B91C1C] text-white font-semibold btn-red-glow"
      >
        <Save size={16} /> {saving ? "Saving..." : "Save Settings"}
      </button>
    </div>
  );
}

function Field({ k, label, textarea, rows = 3, ph, form, set }) {
  const cls = "w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-amber-400/50 text-sm";
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-slate-400 font-semibold block mb-1.5">{label}</label>
      {textarea ? (
        <textarea data-testid={`course-form-${k}`} rows={rows} value={form[k] || ""} placeholder={ph} onChange={(e) => set(k, e.target.value)} className={cls} />
      ) : (
        <input data-testid={`course-form-${k}`} value={form[k] || ""} placeholder={ph} onChange={(e) => set(k, e.target.value)} className={cls} />
      )}
    </div>
  );
}

const EMPTY_COURSE = {
  title: "",
  slug: "",
  short_description: "",
  description: "",
  thumbnail_url: "",
  category: "Skills",
  instructor: "",
  price: "₹99",
  original_price: "₹999",
  tags: [],
  meta_title: "",
  meta_description: "",
  meta_keywords: "",
  reviews: [],
  is_featured: false,
};

function slugify(text) {
  return (text || "")
    .toLowerCase()
    .trim()
    .replace(/[\s_]+/g, "-")
    .replace(/[^a-z0-9\-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");
}

function CourseForm({ initial, onCancel, onSaved }) {
  const [form, setForm] = useState({ ...EMPTY_COURSE, ...initial });
  const [tagsInput, setTagsInput] = useState((initial?.tags || []).join(", "));
  const [reviewsRaw, setReviewsRaw] = useState(JSON.stringify(initial?.reviews || [], null, 2));
  const [saving, setSaving] = useState(false);
  const [manualSlug, setManualSlug] = useState(Boolean(initial?.slug));

  const set = (k, v) => {
    setForm((f) => {
      const next = { ...f, [k]: v };
      // Auto-generate slug from title if user hasn't manually edited slug
      if (k === "title" && !manualSlug && !initial?.id) {
        next.slug = slugify(v);
      }
      return next;
    });
  };

  const submit = async () => {
    const cleanTitle = (form.title || "").trim();
    if (!cleanTitle) {
      toast.error("Please enter a course title");
      return;
    }

    const cleanSlug = slugify(form.slug || cleanTitle);
    if (!cleanSlug) {
      toast.error("Please enter a valid URL slug");
      return;
    }

    setSaving(true);
    let reviews = [];
    if (reviewsRaw && reviewsRaw.trim()) {
      try {
        const parsed = JSON.parse(reviewsRaw);
        if (Array.isArray(parsed)) {
          reviews = parsed.map((r) => ({
            name: (r.name || "Student").trim(),
            rating: Number(r.rating) || 5,
            comment: (r.comment || "").trim(),
            role: (r.role || "").trim(),
          }));
        }
      } catch {
        toast.error("Reviews JSON is invalid. Please check format.");
        setSaving(false);
        return;
      }
    }

    const payload = {
      ...form,
      title: cleanTitle,
      slug: cleanSlug,
      category: (form.category || "Govt Exam").trim(),
      instructor: (form.instructor || "AducationOfficial").trim(),
      price: (form.price || "₹99").trim(),
      original_price: (form.original_price || "₹999").trim(),
      short_description: (form.short_description || "").trim(),
      description: (form.description || "").trim(),
      thumbnail_url: (form.thumbnail_url || "").trim(),
      meta_title: (form.meta_title || "").trim(),
      meta_description: (form.meta_description || "").trim(),
      meta_keywords: (form.meta_keywords || "").trim(),
      tags: tagsInput.split(",").map((t) => t.trim()).filter(Boolean),
      reviews,
    };

    try {
      if (initial?.id) {
        await api.put(`/courses/${initial.id}`, payload);
        toast.success("Course updated successfully");
      } else {
        await api.post("/courses", payload);
        toast.success("Course created successfully");
      }
      if (onSaved) await onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save course");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="glass rounded-2xl p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-display font-bold text-lg">{initial?.id ? "Edit Course" : "New Course"}</h3>
        <button onClick={onCancel} className="text-slate-400 hover:text-white"><X size={18} /></button>
      </div>
      <div className="grid md:grid-cols-2 gap-3">
        <Field k="title" label="Title" form={form} set={set} />
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-400 font-semibold block mb-1.5">
            Slug (auto-generated, url-safe)
          </label>
          <input
            data-testid="course-form-slug"
            value={form.slug || ""}
            placeholder="e.g., english-special-by-jaideep"
            onChange={(e) => {
              setManualSlug(true);
              set("slug", slugify(e.target.value));
            }}
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white focus:outline-none focus:border-amber-400/50 text-sm"
          />
          <p className="text-[10px] text-slate-500 mt-1">Preview URL: /courses/{form.slug || "your-slug"}</p>
        </div>
        <Field k="category" label="Category" ph="Govt Exam / Skills" form={form} set={set} />
        <Field k="instructor" label="Instructor" form={form} set={set} />
        <Field k="price" label="Discount Price" form={form} set={set} />
        <Field k="original_price" label="Original Price" form={form} set={set} />
        <Field k="thumbnail_url" label="Thumbnail URL (16:9)" form={form} set={set} />
        <div>
          <label className="text-xs uppercase tracking-widest text-slate-400 font-semibold block mb-1.5">Tags (comma separated)</label>
          <input
            data-testid="course-form-tags"
            value={tagsInput}
            onChange={(e) => setTagsInput(e.target.value)}
            className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm"
          />
        </div>
      </div>
      <Field k="short_description" label="Short Description" textarea rows={2} form={form} set={set} />
      <Field k="description" label="Full Description" textarea rows={6} form={form} set={set} />
      <Field k="meta_title" label="Meta Title (SEO)" form={form} set={set} />
      <Field k="meta_description" label="Meta Description (SEO)" textarea rows={2} form={form} set={set} />
      <Field k="meta_keywords" label="Meta Keywords (comma separated)" form={form} set={set} />
      <div>
        <label className="text-xs uppercase tracking-widest text-slate-400 font-semibold block mb-1.5">Reviews (JSON array)</label>
        <textarea
          data-testid="course-form-reviews"
          rows={5}
          value={reviewsRaw}
          onChange={(e) => setReviewsRaw(e.target.value)}
          className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-xs font-mono-tab"
        />
        <p className="text-[10px] text-slate-500 mt-1">Format: {`[{"name":"...","rating":5,"comment":"...","role":"..."}]`}</p>
      </div>
      <div className="p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
        <label className="flex items-center gap-2 text-sm text-slate-200 cursor-pointer">
          <input
            type="checkbox"
            checked={form.is_featured}
            onChange={(e) => set("is_featured", e.target.checked)}
            data-testid="course-form-featured"
            className="w-4 h-4 rounded text-amber-500"
          />
          <span className="font-semibold text-white">Show on Home Page (Featured Course)</span>
        </label>
        <p className="text-[11px] text-slate-400 mt-1 ml-6">
          Check this box so this course appears immediately on the main homepage.
        </p>
      </div>
      <div className="flex gap-3">
        <button
          data-testid="course-form-save"
          onClick={submit}
          disabled={saving}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-gradient-to-r from-[#E50914] to-[#B91C1C] text-white font-semibold"
        >
          <Save size={16} /> {saving ? "Saving..." : "Save Course"}
        </button>
        <button onClick={onCancel} className="px-5 py-2.5 rounded-lg border border-white/10 text-slate-300">Cancel</button>
      </div>
    </div>
  );
}

function CoursesPanel() {
  const [courses, setCourses] = useState([]);
  const [editing, setEditing] = useState(null); // {} for new, or course object
  const [dragId, setDragId] = useState(null);
  const [savingOrder, setSavingOrder] = useState(false);
  const load = async () => {
    try {
      const r = await api.get(`/courses?_t=${Date.now()}`);
      setCourses(r.data);
    } catch (err) {
      console.error("Failed to load courses:", err);
    }
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!confirm("Delete this course?")) return;
    await api.delete(`/courses/${id}`);
    toast.success("Deleted");
    await load();
  };

  const onDragStart = (id) => setDragId(id);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (targetId) => {
    if (!dragId || dragId === targetId) return;
    const fromIdx = courses.findIndex((c) => c.id === dragId);
    const toIdx = courses.findIndex((c) => c.id === targetId);
    if (fromIdx < 0 || toIdx < 0) return;
    const next = [...courses];
    const [moved] = next.splice(fromIdx, 1);
    next.splice(toIdx, 0, moved);
    setCourses(next);
    setDragId(null);
  };

  const saveOrder = async () => {
    setSavingOrder(true);
    try {
      await api.post("/courses/reorder", { order: courses.map((c) => c.id) });
      toast.success("Order saved");
      load();
    } catch (e) {
      toast.error("Failed to save order");
    } finally {
      setSavingOrder(false);
    }
  };

  const [syncing, setSyncing] = useState(false);

  const syncOfficial = async () => {
    if (!confirm("Sync all 51 official courses from aducationofficial.in? This will import/update all courses with thumbnails, SEO meta, and reviews.")) return;
    setSyncing(true);
    try {
      const res = await api.post("/courses/sync-official");
      toast.success(`Synced successfully! Inserted: ${res.data.inserted}, Updated: ${res.data.updated}`);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to sync courses");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-display font-bold text-xl">Courses ({courses.length})</h2>
        <div className="flex gap-2 flex-wrap">
          <button
            data-testid="courses-sync-official-btn"
            onClick={syncOfficial}
            disabled={syncing}
            className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg border border-amber-400/30 bg-amber-400/10 text-amber-300 hover:bg-amber-400/20 text-sm font-medium transition"
          >
            <RefreshCw size={14} className={syncing ? "animate-spin" : ""} /> {syncing ? "Syncing..." : "Sync Official Courses (51)"}
          </button>
          <button
            data-testid="courses-save-order-btn"
            onClick={saveOrder}
            disabled={savingOrder}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-white/10 text-slate-300 hover:text-white hover:border-amber-400/40 text-sm font-medium"
          >
            <Save size={14} /> {savingOrder ? "Saving..." : "Save Order"}
          </button>
          <button
            data-testid="courses-new-btn"
            onClick={() => setEditing({})}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-[#F59E0B] to-[#D97706] text-black font-semibold text-sm"
          >
            <Plus size={16} /> New Course
          </button>
        </div>
      </div>

      <p className="text-xs text-slate-500">
        <GripVertical size={12} className="inline -mt-0.5" /> Drag the handle to reorder. Click "Save Order" to persist. Order affects Home carousel and Footer list.
      </p>

      {editing !== null && (
        <CourseForm
          initial={editing}
          onCancel={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      )}

      <div className="space-y-2">
        {courses.map((c) => (
          <div
            key={c.id}
            draggable
            onDragStart={() => onDragStart(c.id)}
            onDragOver={onDragOver}
            onDrop={() => onDrop(c.id)}
            data-testid={`admin-course-row-${c.slug}`}
            className={`glass rounded-xl p-4 flex items-center gap-4 transition ${dragId === c.id ? "opacity-50 scale-[0.99]" : ""}`}
          >
            <button
              className="cursor-grab active:cursor-grabbing text-slate-500 hover:text-amber-400 shrink-0"
              data-testid={`course-drag-${c.slug}`}
              aria-label="Drag to reorder"
            >
              <GripVertical size={18} />
            </button>
            <img src={c.thumbnail_url} alt="" className="w-24 aspect-[16/9] object-cover rounded-lg" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-white truncate">{c.title}</div>
              <div className="text-xs text-slate-400">/{c.slug} · {c.category} · {c.price}</div>
            </div>
            {c.is_featured && <span className="gold-pill text-[10px] font-bold px-2 py-0.5 rounded">FEATURED</span>}
            <button
              data-testid={`course-edit-${c.slug}`}
              onClick={() => setEditing(c)}
              className="p-2 rounded-lg text-slate-400 hover:text-amber-400 hover:bg-white/5"
            >
              <Edit3 size={16} />
            </button>
            <button
              data-testid={`course-delete-${c.slug}`}
              onClick={() => del(c.id)}
              className="p-2 rounded-lg text-slate-400 hover:text-red-400 hover:bg-white/5"
            >
              <Trash2 size={16} />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

function TestimonialsPanel() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ name: "", location: "", role: "", comment: "", avatar_url: "", direction: "ltr" });
  const load = () => api.get("/testimonials").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.name || !form.comment) return toast.error("Name and comment required");
    await api.post("/testimonials", form);
    setForm({ name: "", location: "", role: "", comment: "", avatar_url: "", direction: "ltr" });
    toast.success("Added");
    load();
  };

  const del = async (id) => {
    await api.delete(`/testimonials/${id}`);
    load();
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="font-display font-bold text-xl">Testimonials ({items.length})</h2>
      </div>

      <div className="glass rounded-2xl p-6 grid md:grid-cols-2 gap-3">
        <input placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm" />
        <input placeholder="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm" />
        <input placeholder="Role (e.g., SDE @ Google)" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm" />
        <input placeholder="Avatar URL" value={form.avatar_url} onChange={(e) => setForm({ ...form, avatar_url: e.target.value })} className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm" />
        <textarea placeholder="Comment" rows={3} value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} className="md:col-span-2 px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm" />
        <select value={form.direction} onChange={(e) => setForm({ ...form, direction: e.target.value })} className="px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-white text-sm">
          <option value="ltr">Row 1 (Left → Right)</option>
          <option value="rtl">Row 2 (Right → Left)</option>
        </select>
        <button onClick={add} className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gradient-to-r from-[#E50914] to-[#B91C1C] text-white font-semibold text-sm">
          <Plus size={16} /> Add Testimonial
        </button>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        {items.map((t) => (
          <div key={t.id} className="glass rounded-xl p-4 flex gap-3">
            {t.avatar_url && <img src={t.avatar_url} className="w-12 h-12 rounded-full object-cover" alt="" />}
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-white text-sm">{t.name} <span className="text-slate-500 text-xs">· {t.location}</span></div>
              <div className="text-xs text-amber-400">{t.role}</div>
              <p className="text-xs text-slate-400 mt-1 line-clamp-2">{t.comment}</p>
            </div>
            <button onClick={() => del(t.id)} className="text-slate-400 hover:text-red-400 self-start"><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminDashboardPage() {
  const { user, checking, logout } = useAuth();
  const [tab, setTab] = useState("settings");

  if (checking) return <div className="p-10 text-slate-400">Loading...</div>;
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;

  return (
    <div data-testid="admin-dashboard-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-amber-400 font-bold mb-1">Admin Console</div>
          <h1 className="font-display font-black text-3xl lg:text-4xl">Aducation <span className="red-text">Dashboard</span></h1>
          <div className="text-xs text-slate-400 mt-1">Signed in as {user.email}</div>
        </div>
        <button onClick={logout} className="text-sm text-slate-300 hover:text-white px-3 py-2 rounded-lg border border-white/10">Sign Out</button>
      </div>

      <div className="flex gap-2 mb-8 overflow-x-auto hide-scrollbar">
        <TabButton active={tab === "settings"} onClick={() => setTab("settings")} icon={Cog} label="Settings" testId="tab-settings" />
        <TabButton active={tab === "courses"} onClick={() => setTab("courses")} icon={BookOpen} label="Courses" testId="tab-courses" />
        <TabButton active={tab === "testimonials"} onClick={() => setTab("testimonials")} icon={MessageSquare} label="Testimonials" testId="tab-testimonials" />
      </div>

      {tab === "settings" && <SettingsPanel />}
      {tab === "courses" && <CoursesPanel />}
      {tab === "testimonials" && <TestimonialsPanel />}
    </div>
  );
}
